import { FC } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import FileuploadPickerInput from 'shared/components/FileuploadPicker/FilePickerInput/FileuploadPickerInput'
import SkeletonFileInput from 'shared/components/skeletons/SkeletonFileInput'
import { maxEmailFileSize } from 'shared/constants/email'
import {
  SEND_EMAIL_UPDATE_ATTACHMENTS_ERROR,
  SEND_EMAIL_UPDATE_ATTACHMENTS_REQUESTED,
} from 'shared/store/sendMessage/email/actionTypes'
import { getSendEmail } from 'shared/store/sendMessage/email/selectors'
import { mapEmailAttachmentsToFileWrappers } from './utils'
import { unsupportedMessageFileTypes } from 'Modules/Communication/Messages/shared/constants'
import {
  useDeleteEmailAttachmentMutation,
  useUploadEmailAttachmentsMutation,
} from 'shared/microServices/email/emails.service'
import { FileWrapper } from 'shared/components/FileuploadPicker/types/types'

type SendEmailAttachmentsProps = {
  savingAttachments: boolean
  onSaving: (saving: boolean) => void
}

const SendEmailAttachments: FC<SendEmailAttachmentsProps> = ({
  savingAttachments,
  onSaving,
}) => {
  const {
    message,
    messageId,
    savingAttachments: savingAttachmentsFromStore,
  } = useSelector(getSendEmail)
  const dispatch = useDispatch()

  const [uploadEmailAttachments] = useUploadEmailAttachmentsMutation()
  const [deleteEmailAttachment] = useDeleteEmailAttachmentMutation()

  // Dispatches so that we refetch the email and get the updated attachments. This will also handle dispatch of Success.
  const dispatchEmailUpdateAttachmentsRequested = () => {
    dispatch({
      type: SEND_EMAIL_UPDATE_ATTACHMENTS_REQUESTED,
      // Dont need files or originalFiles anymore, this just triggers a refetch and disaptches success
      payload: {
        files: [],
        id: messageId,
        originalFiles: message?.attachments || [],
      },
    })
  }

  const dispatchEmailUpdateAttachmentsError = (error: string) => {
    dispatch({
      type: SEND_EMAIL_UPDATE_ATTACHMENTS_ERROR,
      payload: error || 'Det skjedde en feil ved lagring av vedlegg',
    })
  }

  const handleAttachmentsChange = async (files: FileWrapper[]) => {
    const localUploadedFiles = files
      .filter((file) => file.isLocal)
      .map((f) => f.file!)

    const archiveFiles = files.filter((file) => file.isArchive)
    const archiveFileIds = archiveFiles.map((file) => file.id)

    // If no new files to upload, just return
    if (localUploadedFiles.length === 0 && archiveFileIds.length === 0) {
      return
    }

    onSaving(true)

    const formData = new FormData()
    localUploadedFiles.map((file: File) => formData.append('files', file))
    archiveFileIds.map((id) => formData.append('archiveDocumentIds', id))

    const uploadResult = await uploadEmailAttachments({
      id: messageId,
      attachments: formData,
    })

    if (!uploadResult.error) {
      dispatchEmailUpdateAttachmentsRequested()
    }

    if (uploadResult.error) {
      dispatchEmailUpdateAttachmentsError(uploadResult.error as string)
    }

    onSaving(false)
  }

  const handleFileRemoved = async (file: FileWrapper) => {
    const res = await deleteEmailAttachment({
      messageId: messageId,
      attachmentId: file.id,
    })

    if (res.error) {
      dispatchEmailUpdateAttachmentsError(res.error as string)
    }

    if (!res?.error) {
      dispatchEmailUpdateAttachmentsRequested()
    }
  }

  const reject = unsupportedMessageFileTypes.join(',')

  return (
    <div className="w-[100%] max-w-[350px]">
      {savingAttachments || savingAttachmentsFromStore ? (
        <SkeletonFileInput length={message?.attachments?.length} />
      ) : (
        <div className="flex flex-col gap-4">
          <FileuploadPickerInput
            initialFiles={mapEmailAttachmentsToFileWrappers(
              message?.attachments
            )}
            controlled
            onChange={handleAttachmentsChange}
            onFileRemoved={handleFileRemoved}
            maxSizeInBytes={maxEmailFileSize}
            reject={reject}
          />
        </div>
      )}
    </div>
  )
}

export default SendEmailAttachments
